import { Box, Fab, Typography, useMediaQuery, useTheme } from '@mui/material';
import { mobileShareableLinkStyles, mobileShareLinkCompactStyles } from './BlogDetailsPage.styles';
import { TSocialMediaLink, defaultSocialMediaLinks } from './SocialMediaLinks';
import TAGLink from '@shared-components/components/TAGLink';
import TAGSvgIcon from '@shared-components/components/TAGSvgIcon';
import { Maybe } from '@shared-components/types';
const SOCIAL_MEDIA_NAMES = new Set(['Facebook', 'X', 'Threads', 'Reddit', 'LinkedIn', 'Email']);
interface ISocialMediaShareLinksProps {
  mediaLinks?: Maybe<TSocialMediaLink[]>;
  isCompactDesign?: Maybe<boolean>;
  iconColor?: Maybe<string>;
  hideIconTitleOnMobile?: Maybe<boolean>;
}
interface ISocialMediaShareLinkItemProps {
  link: TSocialMediaLink;
  isCompactDesign: boolean;
  isMobileScreen: boolean;
  iconColorString: string;
  hideIconTitleOnMobile: boolean;
}
function getShareableLinkPath(linkUrl: string) {
  if (globalThis?.location?.href) {
    return linkUrl + globalThis.location.href;
  }
  return linkUrl;
}
function SocialMediaShareLinkItem({
  link,
  isCompactDesign,
  isMobileScreen,
  iconColorString,
  hideIconTitleOnMobile
}: Readonly<ISocialMediaShareLinkItemProps>) {
  const labelId = `shareable_link_label_${link.name}`;
  const hasIcon = SOCIAL_MEDIA_NAMES.has(link.name);
  const showMobileIconTitle = !hideIconTitleOnMobile && hasIcon && isMobileScreen;
  const dataTestId = isMobileScreen ? `shareable_mobile_link_${link.name}` : `shareable_desktop_link_${link.name}`;
  if (isCompactDesign) {
    return <TAGLink href={getShareableLinkPath(link.url)} openInNewTab noLinkStyle key={link.name} aria-label={`${link.name} social media link`} dataTestId={dataTestId} title={link.name}>
				<Box sx={isMobileScreen ? mobileShareLinkCompactStyles : null}>
					{hasIcon && <TAGSvgIcon htmlColor={iconColorString} fill={iconColorString} icon={link.icon} size={24} viewBox="0 0 24 24" />}
				</Box>
			</TAGLink>;
  }
  return <TAGLink href={getShareableLinkPath(link.url)} openInNewTab noLinkStyle key={link.name} aria-label={`${link.name} social media link`} dataTestId={dataTestId} title={link.name} data-sentry-element="TAGLink" data-sentry-component="SocialMediaShareLinkItem" data-sentry-source-file="SocialMediaShareLinks.tsx">
			<Box sx={isMobileScreen ? mobileShareableLinkStyles : null} data-sentry-element="Box" data-sentry-source-file="SocialMediaShareLinks.tsx">
				<Fab size="S" variant="alternative" data-testid={isMobileScreen ? `mobile_social_media_button_${link.icon}` : `desktop_social_media_button_${link.icon}`} aria-labelledby={isMobileScreen ? labelId : undefined} aria-label={isMobileScreen ? undefined : link.name} tabIndex={-1} component="span" aria-hidden="true" data-sentry-element="Fab" data-sentry-source-file="SocialMediaShareLinks.tsx">
					{hasIcon && <TAGSvgIcon htmlColor={iconColorString} fill={iconColorString} icon={link.icon} size={24} viewBox="0 0 24 24" />}
				</Fab>
				{(!hasIcon || showMobileIconTitle) && <Typography data-testid={`mobile_shareable_link_text_${link.name}`} variant={hasIcon ? 'assistiveText' : 'bodySmallBook'} color="text.primary" id={labelId}>
						{link.name}
					</Typography>}
			</Box>
		</TAGLink>;
}
const SocialMediaShareLinks = ({
  mediaLinks,
  isCompactDesign = false,
  iconColor = '',
  hideIconTitleOnMobile = false
}: ISocialMediaShareLinksProps) => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md'));
  const links = mediaLinks ?? defaultSocialMediaLinks;
  const iconColorString: string = iconColor ?? '';
  return <>
			{links.map(link => <SocialMediaShareLinkItem key={link.name} link={link} isCompactDesign={Boolean(isCompactDesign)} isMobileScreen={isMobileScreen} iconColorString={iconColorString} hideIconTitleOnMobile={Boolean(hideIconTitleOnMobile)} />)}
		</>;
};
export default SocialMediaShareLinks;